/**
 * Human Body Detection Extension for Scratch
 * =============================================
 * Implements PictoBlox-compatible human body detection blocks using
 * TensorFlow.js MoveNet (pose detection) and MediaPipe Hands (hand detection).
 *
 * Features: pose detection (17 body keypoints), hand detection (21 keypoints),
 *           bounding box overlay, multi-person support.
 *
 * Block opcodes, field names, menu names, and display text all match
 * PictoBlox's .sb3 format so that pre-written PictoBlox
 * projects load and display seamlessly.
 */

import * as tf from '@tensorflow/tfjs';
import * as poseDetection from '@tensorflow-models/pose-detection';
import * as handPoseDetection from '@tensorflow-models/hand-pose-detection';
import '@mediapipe/hands';
import formatMessage from 'format-message';

// ── PoseNet / MoveNet keypoint names (17 points) ────────────────
const BODY_PART_MAP = {
    1: 'nose',
    2: 'left_eye',
    3: 'right_eye',
    4: 'left_ear',
    5: 'right_ear',
    6: 'left_shoulder',
    7: 'right_shoulder',
    8: 'left_elbow',
    9: 'right_elbow',
    10: 'left_wrist',
    11: 'right_wrist',
    12: 'left_hip',
    13: 'right_hip',
    14: 'left_knee',
    15: 'right_knee',
    16: 'left_ankle',
    17: 'right_ankle'
};

// ── Hand keypoint mapping (21 points from MediaPipe) ────────────
const HAND_PART_MAP = {
    1: 'wrist',
    2: 'thumb_cmc',
    3: 'thumb_mcp',
    4: 'thumb_ip',
    5: 'thumb_tip',
    6: 'index_finger_mcp',
    7: 'index_finger_pip',
    8: 'index_finger_dip',
    9: 'index_finger_tip',
    10: 'middle_finger_mcp',
    11: 'middle_finger_pip',
    12: 'middle_finger_dip',
    13: 'middle_finger_tip',
    14: 'ring_finger_mcp',
    15: 'ring_finger_pip',
    16: 'ring_finger_dip',
    17: 'ring_finger_tip',
    18: 'pinky_mcp',
    19: 'pinky_pip',
    20: 'pinky_dip',
    21: 'pinky_tip'
};

// Skeleton connections for drawing
const SKELETON_CONNECTIONS = [
    ['left_shoulder', 'right_shoulder'],
    ['left_shoulder', 'left_elbow'],
    ['left_elbow', 'left_wrist'],
    ['right_shoulder', 'right_elbow'],
    ['right_elbow', 'right_wrist'],
    ['left_shoulder', 'left_hip'],
    ['right_shoulder', 'right_hip'],
    ['left_hip', 'right_hip'],
    ['left_hip', 'left_knee'],
    ['left_knee', 'left_ankle'],
    ['right_hip', 'right_knee'],
    ['right_knee', 'right_ankle'],
    ['left_eye', 'nose'],
    ['right_eye', 'nose'],
    ['left_ear', 'left_eye'],
    ['right_ear', 'right_eye']
];

class HumanBodyDetectionExtension {
    constructor (runtime) {
        this.runtime = runtime;

        // ── Internal state ──────────────────────────────────────
        this._poseModelLoaded = false;
        this._poseModelLoading = false;
        this._handModelLoaded = false;
        this._handModelLoading = false;

        this._poseDetector = null;
        this._handDetector = null;

        this._poses = []; // array of pose results
        this._hands = []; // array of hand results
        this._drawBBox = false;
        this._threshold = 0.5;

        // Video / overlay
        this._video = null;
        this._videoReady = false;
        this._overlayCanvas = null;
        this._overlayCtx = null;

        if (this.runtime) {
            this.runtime.on('PROJECT_STOP_ALL', () => {
                this._stopVideo();
            });
        }
        
        // Trigger model loading immediately upon extension initialization
        this._loadModelsInit();
    }

    async _loadModelsInit() {
        if (this.runtime) {
            this.runtime.emit('MODEL_LOADING', 'Human Body Detection');
        }
        await this._loadPoseModel();
        await this._loadHandModel();
        if (this.runtime) {
            this.runtime.emit('MODEL_LOADED', 'Human Body Detection');
        }
    }

    // ═══════════════════════════════════════════════════════════
    //  getInfo — block definitions
    // ═══════════════════════════════════════════════════════════

    _stopVideo () {
        if (this._video && this._video.srcObject) {
            const tracks = this._video.srcObject.getTracks();
            tracks.forEach(track => track.stop());
            this._video.srcObject = null;
        }
        this._videoReady = false;

        if (this._overlayCtx && this._overlayCanvas) {
            this._overlayCtx.clearRect(0, 0, this._overlayCanvas.width, this._overlayCanvas.height);
        }
    }

    getInfo () {
        return {
            id: 'humanBodyDetection',
            name: formatMessage({
                id: 'gui.extension.posenet.name',
                default: 'Human Body Detection'
            }),
            color1: '#9966FF',
            color2: '#774DCB',
            blocks: [
                // ── Commands ────────────────────────────────────
                {
                    opcode: 'toggleStageVideoFeed',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'posenet.blocks.turnVideo',
                        default: 'turn [VIDEO_STATE] video on stage with [TRANSPARENCY] % transparency'
                    }),
                    arguments: {
                        VIDEO_STATE: {
                            type: 'string',
                            menu: 'videoStateMenu',
                            defaultValue: 'on'
                        },
                        TRANSPARENCY: {
                            type: 'number',
                            defaultValue: 0
                        }
                    }
                },
                {
                    opcode: 'drawBoundingBox',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'objectDetection.blocks.boundingBox',
                        default: '[OPTION] bounding box'
                    }),
                    arguments: {
                        OPTION: {
                            type: 'string',
                            menu: 'showHideMenu',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'analyseImage',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'humanBodyDetection.blocks.analysePose',
                        default: 'analyse pose from [FEED]'
                    }),
                    arguments: {
                        FEED: {
                            type: 'string',
                            menu: 'feedMenu',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'analyseHand',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'humanBodyDetection.blocks.analyseHand',
                        default: 'analyse hand and body from [FEED]'
                    }),
                    arguments: {
                        FEED: {
                            type: 'string',
                            menu: 'feedMenu',
                            defaultValue: '1'
                        }
                    }
                },

                // ── Reporters (Pose) ────────────────────────────
                {
                    opcode: 'getNumberPeople',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'humanBodyDetection.blocks.getNumPeople',
                        default: 'get # people'
                    })
                },
                {
                    opcode: 'getBodyPartX',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'humanBodyDetection.blocks.getBodyX',
                        default: 'get x of [BODY_PART] of person [PERSON]'
                    }),
                    arguments: {
                        BODY_PART: {
                            type: 'string',
                            menu: 'bodyPartMenu',
                            defaultValue: '1'
                        },
                        PERSON: {
                            type: 'string',
                            menu: 'personNumber',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'getBodyPartY',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'humanBodyDetection.blocks.getBodyY',
                        default: 'get y of [BODY_PART] of person [PERSON]'
                    }),
                    arguments: {
                        BODY_PART: {
                            type: 'string',
                            menu: 'bodyPartMenu',
                            defaultValue: '1'
                        },
                        PERSON: {
                            type: 'string',
                            menu: 'personNumber',
                            defaultValue: '1'
                        }
                    }
                },

                // ── Reporters (Hand) ────────────────────────────
                {
                    opcode: 'getNumberHands',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'humanBodyDetection.blocks.getNumHands',
                        default: 'get # hands'
                    })
                },
                {
                    opcode: 'getHandPartX',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'humanBodyDetection.blocks.getHandX',
                        default: 'get x of [HAND_PART] of hand [HAND]'
                    }),
                    arguments: {
                        HAND_PART: {
                            type: 'string',
                            menu: 'handPartMenu',
                            defaultValue: '1'
                        },
                        HAND: {
                            type: 'string',
                            menu: 'handNumber',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'getHandPartY',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'humanBodyDetection.blocks.getHandY',
                        default: 'get y of [HAND_PART] of hand [HAND]'
                    }),
                    arguments: {
                        HAND_PART: {
                            type: 'string',
                            menu: 'handPartMenu',
                            defaultValue: '1'
                        },
                        HAND: {
                            type: 'string',
                            menu: 'handNumber',
                            defaultValue: '1'
                        }
                    }
                },

                // ── Booleans ────────────────────────────────────
                {
                    opcode: 'isPersonDetected',
                    blockType: 'Boolean',
                    text: formatMessage({
                        id: 'humanBodyDetection.blocks.isPersonDetected',
                        default: 'is person detected?'
                    })
                },
                {
                    opcode: 'isBodyPartDetected',
                    blockType: 'Boolean',
                    text: formatMessage({
                        id: 'humanBodyDetection.blocks.isBodyPartDetected',
                        default: 'is [BODY_PART] of person [PERSON] detected?'
                    }),
                    arguments: {
                        BODY_PART: {
                            type: 'string',
                            menu: 'bodyPartMenu',
                            defaultValue: '1'
                        },
                        PERSON: {
                            type: 'string',
                            menu: 'personNumber',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'isHandDetected',
                    blockType: 'Boolean',
                    text: formatMessage({
                        id: 'humanBodyDetection.blocks.isHandDetected',
                        default: 'is hand detected?'
                    })
                }
            ],
            menus: {
                feedMenu: {
                    items: [
                        {text: 'camera', value: '1'},
                        {text: 'stage', value: '2'}
                    ]
                },
                videoStateMenu: {
                    items: ['on', 'off']
                },
                showHideMenu: {
                    items: [
                        {text: 'show', value: '1'},
                        {text: 'hide', value: '0'}
                    ]
                },
                bodyPartMenu: {
                    items: [
                        {text: 'nose', value: '1'},
                        {text: 'left eye', value: '2'},
                        {text: 'right eye', value: '3'},
                        {text: 'left ear', value: '4'},
                        {text: 'right ear', value: '5'},
                        {text: 'left shoulder', value: '6'},
                        {text: 'right shoulder', value: '7'},
                        {text: 'left elbow', value: '8'},
                        {text: 'right elbow', value: '9'},
                        {text: 'left wrist', value: '10'},
                        {text: 'right wrist', value: '11'},
                        {text: 'left hip', value: '12'},
                        {text: 'right hip', value: '13'},
                        {text: 'left knee', value: '14'},
                        {text: 'right knee', value: '15'},
                        {text: 'left ankle', value: '16'},
                        {text: 'right ankle', value: '17'}
                    ]
                },
                handPartMenu: {
                    items: [
                        {text: 'wrist', value: '1'},
                        {text: 'thumb cmc', value: '2'},
                        {text: 'thumb mcp', value: '3'},
                        {text: 'thumb ip', value: '4'},
                        {text: 'thumb tip', value: '5'},
                        {text: 'index finger mcp', value: '6'},
                        {text: 'index finger pip', value: '7'},
                        {text: 'index finger dip', value: '8'},
                        {text: 'index finger tip', value: '9'},
                        {text: 'middle finger mcp', value: '10'},
                        {text: 'middle finger pip', value: '11'},
                        {text: 'middle finger dip', value: '12'},
                        {text: 'middle finger tip', value: '13'},
                        {text: 'ring finger mcp', value: '14'},
                        {text: 'ring finger pip', value: '15'},
                        {text: 'ring finger dip', value: '16'},
                        {text: 'ring finger tip', value: '17'},
                        {text: 'pinky mcp', value: '18'},
                        {text: 'pinky pip', value: '19'},
                        {text: 'pinky dip', value: '20'},
                        {text: 'pinky tip', value: '21'}
                    ]
                },
                personNumber: {
                    acceptReporters: true,
                    items: ['1', '2', '3', '4', '5']
                },
                handNumber: {
                    acceptReporters: true,
                    items: ['1', '2']
                }
            }
        };
    }

    // ═══════════════════════════════════════════════════════════
    //  Private helpers
    // ═══════════════════════════════════════════════════════════

    /** Load the MoveNet pose detection model (once). */
    async _loadPoseModel () {
        if (this._poseModelLoaded || this._poseModelLoading) return;
        this._poseModelLoading = true;
        try {
            // Ensure TF.js backend is ready
            await tf.ready();
            this._poseDetector = await poseDetection.createDetector(
                poseDetection.SupportedModels.MoveNet,
                {
                    modelType: poseDetection.movenet.modelType.MULTIPOSE_LIGHTNING,
                    enableSmoothing: true,
                    minPoseScore: this._threshold
                }
            );
            this._poseModelLoaded = true;
            console.log('[HumanBodyDetection] Pose model loaded successfully.');
        } catch (err) {
            console.error('[HumanBodyDetection] Pose model loading failed:', err);
        }
        this._poseModelLoading = false;
    }

    /** Load hand-pose-detection model via TF.js (once). */
    async _loadHandModel () {
        if (this._handModelLoaded || this._handModelLoading) return;
        this._handModelLoading = true;
        try {
            await tf.ready();
            const model = handPoseDetection.SupportedModels.MediaPipeHands;
            const detectorConfig = {
                runtime: 'mediapipe',
                solutionPath: 'https://cdn.jsdelivr.net/npm/@mediapipe/hands',
                modelType: 'full'
            };
            this._handDetector = await handPoseDetection.createDetector(model, detectorConfig);
            this._handModelLoaded = true;
            console.log('[HumanBodyDetection] Hand detection ready.');
        } catch (err) {
            console.error('[HumanBodyDetection] Hand model loading failed:', err);
        }
        this._handModelLoading = false;
    }

    /** Create or return the hidden <video> element for webcam capture. */
    async _ensureVideo () {
        if (this._video && this._videoReady) return this._video;
        if (!this._video) {
            this._video = document.createElement('video');
            this._video.setAttribute('autoplay', '');
            this._video.setAttribute('playsinline', '');
            this._video.width = 480;
            this._video.height = 360;
            this._video.style.display = 'none';
            document.body.appendChild(this._video);
        }
        if (!this._videoReady) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {width: 480, height: 360}
                });
                this._video.srcObject = stream;
                await this._video.play();
                this._videoReady = true;
            } catch (err) {
                console.warn('[HumanBodyDetection] Camera access denied:', err);
            }
        }
        return this._video;
    }

    /** Get or create the transparent overlay canvas on top of the stage. */
    _ensureOverlay () {
        if (this._overlayCanvas) return this._overlayCanvas;
        const stageCanvas =
            document.querySelector('[class*="stage_stage_"] canvas') ||
            document.querySelector('canvas');
        if (!stageCanvas) return null;
        const parent = stageCanvas.parentElement;
        this._overlayCanvas = document.createElement('canvas');
        this._overlayCanvas.width = stageCanvas.width;
        this._overlayCanvas.height = stageCanvas.height;
        this._overlayCanvas.style.cssText =
            'position:absolute;top:0;left:0;width:100%;height:100%;' +
            'pointer-events:none;z-index:100;';
        parent.style.position = 'relative';
        parent.appendChild(this._overlayCanvas);
        this._overlayCtx = this._overlayCanvas.getContext('2d');
        return this._overlayCanvas;
    }

    /** Clear the overlay canvas. */
    _clearOverlay () {
        if (this._overlayCtx && this._overlayCanvas) {
            this._overlayCtx.clearRect(
                0, 0, this._overlayCanvas.width, this._overlayCanvas.height
            );
        }
    }

    /** Draw skeleton + keypoints on the overlay. */
    _renderOverlay () {
        if (!this._drawBBox) {
            this._clearOverlay();
            return;
        }
        this._ensureOverlay();
        if (!this._overlayCtx) return;
        this._clearOverlay();

        const ctx = this._overlayCtx;
        const cW = this._overlayCanvas.width;
        const cH = this._overlayCanvas.height;
        const srcW = (this._video && this._video.videoWidth) ? this._video.videoWidth : 480;
        const srcH = (this._video && this._video.videoHeight) ? this._video.videoHeight : 360;
        const scaleX = cW / srcW;
        const scaleY = cH / srcH;

        // Draw poses
        this._poses.forEach(pose => {
            const keypoints = pose.keypoints || [];

            // Build a keypoint lookup by name
            const kpMap = {};
            keypoints.forEach(kp => {
                kpMap[kp.name] = kp;
            });

            // Draw skeleton lines
            ctx.strokeStyle = '#00ff00';
            ctx.lineWidth = 2;
            SKELETON_CONNECTIONS.forEach(([a, b]) => {
                const ptA = kpMap[a];
                const ptB = kpMap[b];
                if (ptA && ptB && (ptA.score || 0) > 0.3 && (ptB.score || 0) > 0.3) {
                    ctx.beginPath();
                    ctx.moveTo(ptA.x * scaleX, ptA.y * scaleY);
                    ctx.lineTo(ptB.x * scaleX, ptB.y * scaleY);
                    ctx.stroke();
                }
            });

            // Draw keypoints
            keypoints.forEach(kp => {
                if ((kp.score || 0) > 0.3) {
                    ctx.beginPath();
                    ctx.arc(kp.x * scaleX, kp.y * scaleY, 4, 0, 2 * Math.PI);
                    ctx.fillStyle = '#ff0000';
                    ctx.fill();
                    ctx.strokeStyle = '#ffffff';
                    ctx.lineWidth = 1;
                    ctx.stroke();
                }
            });
        });

        // Draw hand keypoints
        this._hands.forEach(hand => {
            const keypoints = hand.keypoints || [];
            keypoints.forEach(kp => {
                if (kp.x !== undefined && kp.y !== undefined) {
                    ctx.beginPath();
                    ctx.arc(kp.x * scaleX, kp.y * scaleY, 3, 0, 2 * Math.PI);
                    ctx.fillStyle = '#00ccff';
                    ctx.fill();
                }
            });

            // Draw hand connections
            const fingerGroups = [
                [0, 1, 2, 3, 4], // thumb
                [0, 5, 6, 7, 8], // index
                [0, 9, 10, 11, 12], // middle
                [0, 13, 14, 15, 16], // ring
                [0, 17, 18, 19, 20] // pinky
            ];
            ctx.strokeStyle = '#00ccff';
            ctx.lineWidth = 1.5;
            fingerGroups.forEach(group => {
                for (let i = 0; i < group.length - 1; i++) {
                    const a = keypoints[group[i]];
                    const b = keypoints[group[i + 1]];
                    if (a && b) {
                        ctx.beginPath();
                        ctx.moveTo(a.x * scaleX, a.y * scaleY);
                        ctx.lineTo(b.x * scaleX, b.y * scaleY);
                        ctx.stroke();
                    }
                }
            });
        });
    }

    /** Find a keypoint by its PictoBlox ID in a specific pose. */
    _getKeypoint (poseIdx, bodyPartId) {
        const pose = this._poses[poseIdx];
        if (!pose || !pose.keypoints) return null;
        const partName = BODY_PART_MAP[bodyPartId];
        if (!partName) return null;
        const kp = pose.keypoints.find(k => k.name === partName);
        return (kp && (kp.score || 0) > 0.3) ? kp : null;
    }

    /** Find a keypoint in a hand detection result. */
    _getHandKeypoint (handIdx, handPartId) {
        const hand = this._hands[handIdx];
        if (!hand || !hand.keypoints) return null;
        const partIdx = (parseInt(handPartId, 10) || 1) - 1;
        const kp = hand.keypoints[partIdx];
        return kp || null;
    }

    // ═══════════════════════════════════════════════════════════
    //  Block implementations
    // ═══════════════════════════════════════════════════════════

    async toggleStageVideoFeed (args) {
        const state = args.VIDEO_STATE;
        const transparency = Math.max(0, Math.min(100, Number(args.TRANSPARENCY) || 0));
        try {
            const video = this.runtime.ioDevices.video;
            if (state === 'on') {
                video.enableVideo();
                await this._ensureVideo();
            } else {
                video.disableVideo();
            }
            video.setPreviewGhost(transparency);
        } catch (err) {
            if (state === 'on') await this._ensureVideo();
        }
    }

    drawBoundingBox (args) {
        this._drawBBox = args.OPTION === '1';
        if (!this._drawBBox) this._clearOverlay();
        else this._renderOverlay();
    }

    async _analyseAll (video) {
        const promises = [];
        if (this._poseDetector) {
            promises.push(this._poseDetector.estimatePoses(video, { maxPoses: 5, flipHorizontal: true })
                .then(poses => { this._poses = poses || []; })
                .catch(err => { console.warn('[HumanBodyDetection] Pose analysis error:', err); this._poses = []; })
            );
        } else {
            this._poses = [];
        }

        if (this._handDetector) {
            promises.push(this._handDetector.estimateHands(video, { flipHorizontal: true })
                .then(hands => { this._hands = hands || []; })
                .catch(err => { console.warn('[HumanBodyDetection] Hand analysis error:', err); this._hands = []; })
            );
        } else {
            this._hands = [];
        }

        await Promise.all(promises);
        this._renderOverlay();
    }

    async analyseImage (args) {
        await Promise.all([this._loadPoseModel(), this._loadHandModel()]);
        const video = await this._ensureVideo();
        if (!video || !this._videoReady) {
            this._poses = [];
            this._hands = [];
            return;
        }
        await this._analyseAll(video);
    }

    async analyseHand (args) {
        await Promise.all([this._loadPoseModel(), this._loadHandModel()]);
        const video = await this._ensureVideo();
        if (!video || !this._videoReady) {
            this._poses = [];
            this._hands = [];
            return;
        }
        await this._analyseAll(video);
    }

    // ── Pose reporters ──────────────────────────────────────────

    getNumberPeople () {
        return this._poses.length;
    }

    getBodyPartX (args) {
        const personIdx = (parseInt(args.PERSON, 10) || 1) - 1;
        const kp = this._getKeypoint(personIdx, args.BODY_PART);
        if (!kp) return 0;
        // Convert from video coords to Scratch coords (-240 to 240) using actual video dimensions
        const vw = (this._video && this._video.videoWidth) ? this._video.videoWidth : 480;
        return Math.round((kp.x / vw * 480) - 240);
    }

    getBodyPartY (args) {
        const personIdx = (parseInt(args.PERSON, 10) || 1) - 1;
        const kp = this._getKeypoint(personIdx, args.BODY_PART);
        if (!kp) return 0;
        // Convert from video coords to Scratch coords (180 to -180) using actual video dimensions
        const vh = (this._video && this._video.videoHeight) ? this._video.videoHeight : 360;
        return Math.round(180 - (kp.y / vh * 360));
    }

    // ── Hand reporters ──────────────────────────────────────────

    getNumberHands () {
        return this._hands.length;
    }

    getHandPartX (args) {
        const handIdx = (parseInt(args.HAND, 10) || 1) - 1;
        const kp = this._getHandKeypoint(handIdx, args.HAND_PART);
        if (!kp) return 0;
        const vw = (this._video && this._video.videoWidth) ? this._video.videoWidth : 480;
        return Math.round((kp.x / vw * 480) - 240);
    }

    getHandPartY (args) {
        const handIdx = (parseInt(args.HAND, 10) || 1) - 1;
        const kp = this._getHandKeypoint(handIdx, args.HAND_PART);
        if (!kp) return 0;
        const vh = (this._video && this._video.videoHeight) ? this._video.videoHeight : 360;
        return Math.round(180 - (kp.y / vh * 360));
    }

    // ── Booleans ────────────────────────────────────────────────

    isPersonDetected () {
        return this._poses.length > 0;
    }

    isBodyPartDetected (args) {
        const personIdx = (parseInt(args.PERSON, 10) || 1) - 1;
        const kp = this._getKeypoint(personIdx, args.BODY_PART);
        return kp !== null;
    }

    isHandDetected () {
        return this._hands.length > 0;
    }
}

export default HumanBodyDetectionExtension;
